import React, { useState } from 'react';
import { useCms } from '../context/CmsContext';
import useSeo from '../hooks/useSeo';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';
const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

const defaultData = {
  hero: {
    badge: 'Documentação',
    heading: 'Como o Nexus funciona',
    subheading: 'Entenda cada módulo, ferramenta e integração da plataforma Nexus para transformar sua gestão pública.',
  },
  modulos: [
    {
      id: 'operacional',
      icon: 'dashboard',
      color: 'blue',
      title: 'Controle Operacional',
      description: 'O módulo de Controle Operacional centraliza todos os processos da sua organização em um único painel. Defina fluxos de trabalho, atribua responsáveis por setor e acompanhe o status de cada processo em tempo real.',
      features: [
        'Criação e gestão de processos administrativos',
        'Definição de fluxos operacionais customizáveis',
        'Atribuição de setores e responsáveis',
        'Histórico completo de cada processo',
        'Notificações automáticas por etapa',
      ],
      image_url: '',
    },
    {
      id: 'cidadao',
      icon: 'location_on',
      color: 'emerald',
      title: 'Requisição do Cidadão',
      description: 'Canal direto entre o cidadão e o poder público. Requerimentos com geolocalização permitem identificar exatamente onde o problema está no mapa, facilitando o atendimento por proximidade e urgência.',
      features: [
        'Requerimentos com geolocalização no mapa',
        'Painel unificado de todas as requisições',
        'Demandas de comunidades agrupadas por região',
        'Status em tempo real para o cidadão',
        'Histórico de atendimentos por endereço',
      ],
      image_url: '',
    },
    {
      id: 'projetos',
      icon: 'assignment',
      color: 'violet',
      title: 'Gestão de Projetos',
      description: 'Planejamento estratégico com metodologias OKR e KPI integradas. Crie projetos, defina metas, acompanhe indicadores e gere relatórios de produtividade para tomada de decisão baseada em dados.',
      features: [
        'Planejamento Estratégico com KPI e OKR',
        'Projetos com Plano de Ação detalhado',
        'Controle de produtividade por equipe',
        'Painel de indicadores em tempo real',
        'Relatórios exportáveis em PDF e Excel',
      ],
      image_url: '',
    },
  ],
  sei: {
    heading: 'Integração com o SEI!',
    subheading: 'Conectividade nativa com o Sistema Eletrônico de Informações do governo federal.',
    description: 'A Nexus possui integração nativa com o SEI! (Sistema Eletrônico de Informações), o sistema oficial de gestão de documentos e processos administrativos adotado pelo Governo Federal e por centenas de órgãos públicos no Brasil. Com a integração, processos criados no SEI! podem ser sincronizados automaticamente com a Nexus, eliminando retrabalho e garantindo rastreabilidade total.',
    benefits: [
      'Sincronização automática de processos do SEI! com a Nexus',
      'Consulta de documentos e despachos diretamente no painel',
      'Geração de documentos no SEI! a partir de fluxos da Nexus',
      'Rastreabilidade completa entre os dois sistemas',
      'Sem necessidade de entrada manual de dados duplicados',
    ],
    image_url: '',
  },
  onboarding: {
    heading: 'Como começar',
    subheading: 'Em poucos passos sua organização já está operando com a Nexus.',
    steps: [
      { title: 'Solicite uma demonstração', description: 'Nossa equipe apresenta a plataforma personalizada para o contexto da sua organização.' },
      { title: 'Configuração e implantação', description: 'Configuramos os módulos contratados, integrações e importamos seus dados existentes.' },
      { title: 'Treinamento da equipe', description: 'Realizamos treinamento completo com todos os servidores e gestores que vão usar a plataforma.' },
      { title: 'Go live e suporte contínuo', description: 'Sua organização entra em operação com suporte ativo da nossa equipe técnica.' },
    ],
  },
  faq: {
    heading: 'Perguntas Frequentes',
    items: [
      { question: 'O Nexus funciona na nuvem ou precisa de servidor próprio?', answer: 'O Nexus é 100% em nuvem (SaaS). Não é necessário instalar nada nem manter servidores. Acesso via navegador em qualquer dispositivo.' },
      { question: 'Quantos usuários podem usar ao mesmo tempo?', answer: 'Os planos começam em 100 usuários simultâneos. Também oferecemos planos para 200 e 300 usuários conforme a necessidade da organização.' },
      { question: 'Os dados ficam seguros? Há backup?', answer: 'Sim. Utilizamos infraestrutura com criptografia em trânsito e em repouso, backups automáticos diários e conformidade com a LGPD.' },
      { question: 'É possível customizar os fluxos para minha organização?', answer: 'Sim. Os fluxos operacionais, formulários e indicadores são totalmente customizáveis para o contexto de cada órgão.' },
      { question: 'A integração com o SEI! requer alguma configuração especial?', answer: 'A integração requer as credenciais de API do SEI! do seu órgão. Nossa equipe realiza toda a configuração durante a implantação.' },
    ],
  },
};

const colorMap = {
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600',    border: 'border-blue-200',    badge: 'bg-blue-100 text-blue-700' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  violet:  { bg: 'bg-violet-50',  icon: 'text-violet-600',  border: 'border-violet-200',  badge: 'bg-violet-100 text-violet-700' },
};

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{question}</span>
        <span className={`material-icons text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          expand_more
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 bg-white border-t border-gray-100">
          <p className="text-gray-600 text-sm leading-relaxed pt-4">{answer}</p>
        </div>
      )}
    </div>
  );
};

const PlataformaPage = () => {
  useSeo('plataforma');
  const cms = useCms('plataforma', defaultData);
  const hero = cms.hero || defaultData.hero;
  const modulos = cms.modulos || defaultData.modulos;
  const sei = cms.sei || defaultData.sei;
  const onboarding = cms.onboarding || defaultData.onboarding;
  const faq = cms.faq || defaultData.faq;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section
        className="pt-28 pb-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-blue-700/20 rounded-full blur-3xl" />
        </div>
        <div className="relative container mx-auto px-6 md:px-12 text-center">
          <span className="inline-block bg-white/10 border border-white/20 text-blue-200 text-xs font-bold px-4 py-2 rounded-full mb-5 uppercase tracking-widest">
            {hero.badge}
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight">
            {hero.heading}
          </h1>
          <p className="text-blue-100/70 max-w-2xl mx-auto text-lg leading-relaxed">
            {hero.subheading}
          </p>
          {/* Anchor nav */}
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {[
              { href: '#modulos', label: 'Módulos' },
              { href: '#sei', label: 'Integração SEI!' },
              { href: '#como-comecar', label: 'Como começar' },
              { href: '#faq', label: 'FAQ' },
            ].map(({ href, label }) => (
              <a
                key={href}
                href={href}
                onClick={e => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' }); }}
                className="px-4 py-2 bg-white/10 border border-white/20 text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" fill="none" preserveAspectRatio="none" className="w-full h-8">
            <path d="M0 40L1440 40L1440 10C1200 40 960 0 720 15C480 30 240 0 0 10L0 40Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Módulos */}
      <section id="modulos" className="py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-widest">
              Módulos
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
              Três módulos, uma plataforma completa
            </h2>
          </div>

          <div className="space-y-20">
            {modulos.map((mod, idx) => {
              const c = colorMap[mod.color] || colorMap.blue;
              const isEven = idx % 2 === 0;
              return (
                <div
                  key={mod.id || idx}
                  className={`flex flex-col lg:flex-row items-center gap-12 ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Text */}
                  <div className="lg:w-1/2">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-5 ${c.badge}`}>
                      <span className="material-icons text-base">{mod.icon}</span>
                      {mod.title}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
                      {mod.title}
                    </h3>
                    <p className="text-gray-500 leading-relaxed mb-8 text-sm">{mod.description}</p>
                    <ul className="space-y-3">
                      {(mod.features || []).map((feat, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${c.bg} border ${c.border}`}>
                            <svg className={`w-3 h-3 ${c.icon}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-700 text-sm">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Image */}
                  <div className="lg:w-1/2">
                    <div className={`rounded-2xl overflow-hidden border ${c.border} shadow-lg ${c.bg} flex items-center justify-center min-h-64`}>
                      {mod.image_url ? (
                        <img src={resolveUrl(mod.image_url)} alt={mod.title} className="w-full h-auto" />
                      ) : (
                        <div className="text-center py-16 px-8">
                          <span className={`material-icons text-6xl ${c.icon} opacity-30 mb-3 block`}>{mod.icon}</span>
                          <p className="text-gray-400 text-sm">Screenshot do módulo</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEI */}
      <section id="sei" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-5xl mx-auto bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div
                className="lg:w-2/5 p-10 flex flex-col justify-center"
                style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}
              >
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-5">
                  <span className="material-icons text-3xl text-blue-200">sync</span>
                </div>
                <h2 className="text-2xl font-extrabold text-white mb-3 tracking-tight">{sei.heading}</h2>
                <p className="text-blue-200/70 text-sm leading-relaxed">{sei.subheading}</p>
              </div>
              <div className="lg:w-3/5 p-10">
                <p className="text-gray-600 text-sm leading-relaxed mb-8">{sei.description}</p>
                <ul className="space-y-3">
                  {(sei.benefits || []).map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700 text-sm">{b}</span>
                    </li>
                  ))}
                </ul>
                {sei.image_url && (
                  <img src={resolveUrl(sei.image_url)} alt="Integração SEI" className="mt-8 rounded-xl w-full h-auto border border-gray-100" />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding */}
      <section id="como-comecar" className="py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-50 text-emerald-600 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-widest">
              Onboarding
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
              {onboarding.heading}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm">{onboarding.subheading}</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-100 hidden md:block" />
              <div className="space-y-6">
                {(onboarding.steps || []).map((step, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-extrabold text-lg shadow-lg z-10">
                      {i + 1}
                    </div>
                    <div className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:shadow-sm transition-shadow">
                      <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block bg-violet-50 text-violet-600 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-widest">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
              {faq.heading}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {(faq.items || []).map((item, i) => (
              <FaqItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}>
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">Pronto para transformar sua gestão?</h2>
          <p className="text-blue-100/70 mb-8 max-w-xl mx-auto text-sm">Nossa equipe está pronta para apresentar o Nexus para sua organização.</p>
          <a
            href="/#contato"
            className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-8 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5 text-sm"
          >
            Falar com especialista
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlataformaPage;
